import React, { useState, useMemo, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  Button,
  CircularProgress,
  Alert,
  Chip,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  InputAdornment,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { useSearchParams } from 'react-router-dom';
import { useAllPackages } from '../hooks/useCMS';
import { Package } from '../types/cms';
import SafeImage from '../components/SafeImage';
import SEOHead from '../components/SEOHead';

function Paquetes() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { data, loading, error } = useAllPackages();
  const [searchTerm, setSearchTerm] = useState('');
  // Inicializar el estado desde la URL
  const [selectedDestination, setSelectedDestination] = useState(() => {
    return searchParams.get('destino') || '';
  });
  const [priceRange, setPriceRange] = useState('');

  const packages = data?.packages || [];

  // Sincronizar el estado con la URL cuando cambien los parámetros externamente (navegación, etc.)
  useEffect(() => {
    const destinoParam = searchParams.get('destino') || '';
    setSelectedDestination(destinoParam);
  }, [searchParams]);

  // Obtener destinos únicos para el filtro
  const destinations = useMemo(() => {
    const destSet = new Set<string>();
    packages.forEach(pkg => {
      pkg.destinations?.forEach(dest => {
        destSet.add(dest.title);
      });
    });
    return Array.from(destSet).sort();
  }, [packages]);

  // Obtener rangos de precios únicos
  const priceRanges = useMemo(() => {
    const prices = packages.map(pkg => pkg.priceFrom).filter(Boolean).sort((a, b) => a! - b!);
    const ranges = [
      { label: 'Todos los precios', value: '' },
      { label: 'Hasta $50,000', value: '0-50000' },
      { label: '$50,000 - $100,000', value: '50000-100000' },
      { label: '$100,000 - $200,000', value: '100000-200000' },
      { label: 'Más de $200,000', value: '200000-999999' },
    ];
    return ranges;
  }, [packages]);

  // Filtrar paquetes
  const filteredPackages = useMemo(() => {
    return packages.filter(pkg => {
      // Filtro por término de búsqueda
      const matchesSearch = searchTerm === '' || 
        pkg.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        pkg.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        pkg.destinations?.some(dest => 
          dest.title.toLowerCase().includes(searchTerm.toLowerCase())
        );

      // Filtro por destino
      const matchesDestination = selectedDestination === '' ||
        pkg.destinations?.some(dest => dest.title === selectedDestination);

      // Filtro por rango de precio
      let matchesPrice = true;
      if (priceRange && pkg.priceFrom) {
        const [min, max] = priceRange.split('-').map(Number);
        matchesPrice = pkg.priceFrom >= min && pkg.priceFrom <= max;
      }

      return matchesSearch && matchesDestination && matchesPrice;
    });
  }, [packages, searchTerm, selectedDestination, priceRange]);

  const handleDestinationChange = (event: SelectChangeEvent) => {
    const value = event.target.value;
    setSelectedDestination(value);
    
    // Actualizar la URL con el nuevo filtro
    const newSearchParams = new URLSearchParams(searchParams);
    if (value) {
      newSearchParams.set('destino', value);
    } else {
      newSearchParams.delete('destino');
    }
    setSearchParams(newSearchParams, { replace: true });
  };

  const handlePriceRangeChange = (event: SelectChangeEvent) => {
    setPriceRange(event.target.value);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <Alert severity="error">
          Error al cargar los paquetes. Por favor, intenta de nuevo más tarde.
        </Alert>
      </Container>
    );
  }

  const renderPackage = (pkg: Package) => (
    <Card key={pkg.id} sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <SafeImage
        src={pkg.imageCover?.publicUrl}
        alt={pkg.title}
        width="100%"
        height={200}
        sx={{ objectFit: 'cover' }}
      />
      <CardContent sx={{ flexGrow: 1 }}>
        <Typography gutterBottom variant="h6" component="h2">
          {pkg.title}
        </Typography>
        {pkg.description && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {pkg.description}
          </Typography>
        )}
        
        {/* Destinos */}
        <Box sx={{ mb: 2 }}>
          {pkg.destinations?.map((destination) => (
            <Chip
              key={destination.id}
              label={destination.title}
              size="small"
              sx={{ mr: 1, mb: 1 }}
            />
          ))}
        </Box>

        {/* Duración */}
        {(pkg.durationDays || pkg.durationNights) && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {pkg.durationDays && `${pkg.durationDays} días`}
            {pkg.durationDays && pkg.durationNights && ' • '}
            {pkg.durationNights && `${pkg.durationNights} noches`}
          </Typography>
        )}

        {/* Highlights */}
        {pkg.highlights && pkg.highlights.length > 0 && (
          <Box sx={{ mb: 2 }}>
            {pkg.highlights.slice(0, 3).map((highlight, index) => (
              <Chip
                key={index}
                label={highlight}
                size="small"
                variant="outlined"
                sx={{ mr: 1, mb: 1 }}
              />
            ))}
          </Box>
        )}

        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 'auto' }}>
          <Typography variant="h6" color="primary">
            Desde ${pkg.priceFrom?.toLocaleString()?.replace(/,/g, '.')}
          </Typography>
          <Button 
            variant="contained" 
            color="primary"
            href={`/paquetes/${pkg.slug}`}
          >
            Ver detalles
          </Button>
        </Box>
      </CardContent>
    </Card>
  );

  return (
    <>
      <SEOHead
        title="Paquetes de Viaje desde Uruguay"
        description="Descubrí nuestros paquetes de viaje grupales desde Uruguay. Excursiones a Argentina, Brasil y más destinos. Precios accesibles con planes de financiación y cuotas."
        keywords={[
          'paquetes de viaje',
          'excursiones grupales',
          'viajes desde Uruguay',
          'paquetes turísticos',
          'viajes en bus',
          'financiación de viajes',
        ]}
        url="/paquetes"
        type="website"
      />
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <Typography variant="h3" component="h1" gutterBottom sx={{ fontSize: '2rem' }}>
          Paquetes de Viaje
        </Typography>
      
      {/* Filtros */}
      <Box sx={{ mb: 4, p: 3, bgcolor: 'grey.50', borderRadius: 2 }}>
        <Typography variant="h6" gutterBottom>
          Filtros
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="Buscar paquetes"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <FormControl fullWidth>
              <InputLabel>Destino</InputLabel>
              <Select
                value={selectedDestination}
                label="Destino"
                onChange={handleDestinationChange}
              >
                <MenuItem value="">Todos los destinos</MenuItem>
                {destinations.map((dest) => (
                  <MenuItem key={dest} value={dest}>
                    {dest}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={4}>
            <FormControl fullWidth>
              <InputLabel>Rango de precio</InputLabel>
              <Select
                value={priceRange}
                label="Rango de precio"
                onChange={handlePriceRangeChange}
              >
                {priceRanges.map((range) => (
                  <MenuItem key={range.value} value={range.value}>
                    {range.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Box>

      {/* Resultados */}
      <Box sx={{ mb: 2 }}>
        <Typography variant="h6" color="text.secondary">
          {filteredPackages.length} paquete{filteredPackages.length !== 1 ? 's' : ''} encontrado{filteredPackages.length !== 1 ? 's' : ''}
        </Typography>
      </Box>

      {/* Lista de paquetes */}
      {filteredPackages.length > 0 ? (
        <Grid container spacing={3}>
          {filteredPackages.map((pkg) => (
            <Grid item xs={12} sm={6} md={4} key={pkg.id}>
              {renderPackage(pkg)}
            </Grid>
          ))}
        </Grid>
      ) : (
        <Box textAlign="center" py={8}>
          <Typography variant="h5" color="text.secondary">
            No se encontraron paquetes con los filtros seleccionados.
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mt: 2 }}>
            Intenta ajustar los filtros de búsqueda.
          </Typography>
        </Box>
      )}
    </Container>
    </>
  );
}

export default Paquetes;
